import type { ChatSessionModel } from "@/api/models"

import { SettingsService } from "@/services/settings-service"

/**
 * Keeps a brand-new DURABLE chat visible in the sidebar for the whole turn.
 *
 * The sidebar hides untitled sessions (`use-chat-history.ts`:
 * `sessions.filter((s) => s?.title)`), and a freshly created session is
 * untitled server-side (`routes/sessions.py`: `new_title = custom_title ?
 * ... : (title ? title : "")`). Both transports therefore rely on a
 * client-side placeholder title to show the chat while the turn runs — but
 * they differ in how long they need it:
 *
 * * **Legacy** titles the session DURING the turn (`Approach.save_message`
 *   → the `title_state_changed` WebSocket frame patches the list cache with
 *   the real title mid-stream), so from that moment on any refetch returns a
 *   titled row and the chat stays visible on its own.
 * * **Durable** titles the session only at COMPLETION
 *   (`ChatTurnRunner._complete` → `_enrich_and_publish_session`). For the
 *   entire turn the server's row is `title: ""`, so the one-shot optimistic
 *   `{ title: "New chat" }` insert `ensureSession`/`handleSendQuestion` write
 *   into `queryKeys.sessionHistory` is destroyed by the FIRST refetch that
 *   lands — window focus (`refetchOnWindowFocus: true`), a 30s-stale remount,
 *   or any other module's invalidate — and the chat disappears until the user
 *   reloads. Durable turns are exactly the ones users tab away from, so this
 *   fires constantly in practice.
 *
 * This registry is the durable-only repair: while a durable turn is in
 * flight, its session id carries a placeholder title that
 * `useSessionsQuery`'s fetcher re-applies to every freshly fetched list, so
 * no refetch can hide the chat. It NEVER overwrites a real title, so once
 * enrichment names the chat the placeholder is inert, and it is released when
 * the turn settles.
 *
 * Registered from BOTH durable entry points, because both end up watching an
 * untitled in-flight chat:
 *
 * * the send path (`use-chat.ts`'s `sendDurableExplorerChatTurn`), before the
 *   accept round-trip, so a refetch already in flight is covered too; and
 * * `attachDurableChatTurnToSession`, on every non-terminal emit — which is
 *   what a tab that RELOADED mid-turn (or a second viewer) re-enters through.
 *   That tab has no optimistic cache write to inherit at all: its very first
 *   session-list load is the server's untitled row.
 *
 * Both release on the turn's first terminal emit.
 *
 * Flag OFF (and every legacy send) registers nothing, so
 * {@link applyDurableSessionPlaceholders} returns the caller's array by
 * reference and the session-list query behaves exactly as before.
 */
const placeholdersBySessionId = new Map<string, { turnId: string; title: string }>()

const PLACEHOLDER_CHANNEL_NAME = "durable-session-placeholders"
export type PlaceholderChangeSource = "local" | "remote"

const placeholderListeners = new Set<(source: PlaceholderChangeSource) => void>()
let placeholderChannel: BroadcastChannel | null = null
let placeholderTabId: string | null = null
let applyingRemotePlaceholder = false

function currentUserOid(): string | null {
  try {
    const oid: unknown = SettingsService.getInstance().claims.oid
    if (typeof oid !== "string") return null
    const trimmed = oid.trim()
    return trimmed === "" ? null : trimmed
  } catch {
    return null
  }
}

function notifyPlaceholderListeners(): void {
  const source: PlaceholderChangeSource = applyingRemotePlaceholder ? "remote" : "local"
  for (const listener of placeholderListeners) {
    listener(source)
  }
}

function getPlaceholderTabId(): string {
  placeholderTabId ??=
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `tab-${Date.now()}`
  return placeholderTabId
}

/**
 * `register` / `release` mirror one change of the map. `sync` is sent once by
 * a tab when it opens the channel, asking the tabs already open to repeat
 * what they cover — registrations are broadcast only when they CHANGE, so a
 * tab opened mid-turn would otherwise never hear about a turn already
 * running elsewhere. (`sessionId` / `turnId` are empty on `sync`.)
 */
type PlaceholderBroadcast = {
  originTabId: string
  userId: string
  kind: "register" | "release" | "sync"
  sessionId: string
  turnId: string
  title?: string
}

function answerSyncRequest(channel: BroadcastChannel, userId: string): void {
  for (const [sessionId, { turnId, title }] of placeholdersBySessionId) {
    channel.postMessage({
      kind: "register",
      sessionId,
      turnId,
      title,
      originTabId: getPlaceholderTabId(),
      userId,
    } satisfies PlaceholderBroadcast)
  }
}

function getPlaceholderChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null
  if (placeholderChannel) return placeholderChannel
  placeholderChannel = new BroadcastChannel(PLACEHOLDER_CHANNEL_NAME)
  placeholderChannel.onmessage = (event: MessageEvent<unknown>) => {
    const message = event.data
    if (!message || typeof message !== "object") return
    const payload = message as Record<string, unknown>
    if (typeof payload.originTabId !== "string" || typeof payload.userId !== "string") return
    if (typeof payload.sessionId !== "string" || typeof payload.turnId !== "string") return
    if (payload.kind !== "register" && payload.kind !== "release" && payload.kind !== "sync") return
    if (payload.originTabId === getPlaceholderTabId()) return
    const oid = currentUserOid()
    if (!oid || payload.userId !== oid) return
    if (payload.kind === "sync") {
      if (placeholderChannel) answerSyncRequest(placeholderChannel, oid)
      return
    }
    applyingRemotePlaceholder = true
    try {
      if (payload.kind === "register") {
        const title = typeof payload.title === "string" ? payload.title : ""
        registerDurableSessionPlaceholder(payload.sessionId, payload.turnId, title)
      } else {
        releaseDurableSessionPlaceholder(payload.sessionId, payload.turnId)
      }
    } finally {
      applyingRemotePlaceholder = false
    }
  }
  const userId = currentUserOid()
  if (userId) {
    placeholderChannel.postMessage({
      kind: "sync",
      sessionId: "",
      turnId: "",
      originTabId: getPlaceholderTabId(),
      userId,
    } satisfies PlaceholderBroadcast)
  }
  return placeholderChannel
}

function broadcastPlaceholder(message: Omit<PlaceholderBroadcast, "originTabId" | "userId">): void {
  if (applyingRemotePlaceholder) return
  const userId = currentUserOid()
  if (!userId) return
  const channel = getPlaceholderChannel()
  if (!channel) return
  channel.postMessage({
    ...message,
    originTabId: getPlaceholderTabId(),
    userId,
  } satisfies PlaceholderBroadcast)
}

/**
 * Subscribe to placeholder map changes.
 *
 * `local` is this tab's own register/release (the sending tab already has
 * `ensureSession`'s optimistic row — refetching would wipe it). `remote` is
 * another tab's BroadcastChannel update; the sessions query refetches then
 * so `applyDurableSessionPlaceholders` can title an untitled server row.
 */
export function subscribeDurableSessionPlaceholders(
  listener: (source: PlaceholderChangeSource) => void
): () => void {
  placeholderListeners.add(listener)
  getPlaceholderChannel()
  return () => {
    placeholderListeners.delete(listener)
  }
}

/**
 * Start covering `sessionId` with `title` until `turnId`'s durable turn
 * settles.
 *
 * Keyed by session, scoped by turn: a session can only ever have one
 * in-flight durable turn, but a Stop/supersede can hand it to a NEWER turn
 * (a different `turnId`) while an older request for the same session is
 * still winding down. Registering re-stamps the entry with the caller's own
 * `turnId`, so a later {@link releaseDurableSessionPlaceholder} call from a
 * stale request can tell "my own registration" apart from "a newer turn's."
 */
export function registerDurableSessionPlaceholder(
  sessionId: string,
  turnId: string,
  title: string
): void {
  if (!sessionId || !turnId || !title) return
  // Idempotent by contract: `attachDurableChatTurnToSession` re-registers on
  // EVERY streamed emit. Only a real change may notify or broadcast —
  // otherwise each emit made every other tab refetch its session list
  // (issue #3323 item C2: the `list-sessions-batch` flood).
  const current = placeholdersBySessionId.get(sessionId)
  if (current && current.turnId === turnId && current.title === title) return
  placeholdersBySessionId.set(sessionId, { turnId, title })
  notifyPlaceholderListeners()
  broadcastPlaceholder({ kind: "register", sessionId, turnId, title })
}

/**
 * Stop covering `sessionId` — but only if `turnId` is still the one that
 * owns the current registration. A stale request (superseded by a newer
 * send while its own accept round-trip was in flight) must not delete a
 * newer turn's live placeholder just because they share a session id.
 * A no-op when nothing is registered, or when a different turn owns it.
 */
export function releaseDurableSessionPlaceholder(sessionId: string, turnId: string): void {
  if (!sessionId || !turnId) return
  const current = placeholdersBySessionId.get(sessionId)
  if (current && current.turnId === turnId) {
    placeholdersBySessionId.delete(sessionId)
    notifyPlaceholderListeners()
    broadcastPlaceholder({ kind: "release", sessionId, turnId })
  }
}

/** Test-only: drop every placeholder so suites cannot leak into each other. */
export function resetDurableSessionPlaceholders(): void {
  placeholdersBySessionId.clear()
  placeholderListeners.clear()
  if (placeholderChannel) {
    placeholderChannel.close()
    placeholderChannel = null
  }
  placeholderTabId = null
}

/**
 * Fill in the placeholder title for any covered session that came back
 * untitled. Returns `sessions` itself when nothing changed, so an unaffected
 * fetch keeps referential identity (and, with no placeholders registered, is
 * a pure pass-through).
 */
export function applyDurableSessionPlaceholders(sessions: ChatSessionModel[]): ChatSessionModel[] {
  if (placeholdersBySessionId.size === 0) return sessions

  const next = sessions.map((session) => {
    // A real title always wins — the placeholder only ever fills a gap.
    if (!session?.id || session.title) return session
    const title = placeholdersBySessionId.get(session.id)?.title
    if (!title) return session
    return { ...session, title }
  })
  // Same identity-preserving shape as `applyDurableSessionEnrichmentToSessions`.
  return next.some((session, index) => session !== sessions[index]) ? next : sessions
}
